import {isFeatureEnabled} from '@github-ui/feature-flags'
import {LABELS} from '@github-ui/item-picker-base/constants/labels'
import {ItemPicker, type ItemPickerProps} from '@github-ui/item-picker-base/ItemPicker'
import type {ItemGroup} from '@github-ui/item-picker-base/shared'
import {useItemPickerErrorFallback} from '@github-ui/item-picker-base/useItemPickerErrorFallback'
import {clientSideRelayFetchQueryRetained} from '@github-ui/relay-environment'
import {useLayoutTrackingRef} from '@github-ui/use-tracking-ref'
import type React from 'react'
import {type Dispatch, useCallback, useEffect, useEffectEvent, useMemo, useRef, useState} from 'react'
import {graphql, useRelayEnvironment} from 'react-relay'

import {useIssueFiltering} from '../hooks/useIssueFiltering'
import {getIssueIconAndFill} from '../utils/issue-icon'
import type {
  IssuePickerSelectedIssuesQuery,
  IssuePickerSelectedIssuesQuery$data,
} from './__generated__/IssuePickerSelectedIssuesQuery.graphql'
import type {IssuePickerIssueNode} from './issue-picker-selected-issues-query-types'
import styles from './IssuePicker.module.css'
import {useIssuePickerSelectedIssues} from './use-issue-picker-selected-issues'

const getItemKey = (issue: IssuePickerItem) => issue.id

export type IssuePickerItem = IssuePickerIssueNode & {
  parent?: {
    readonly id: string
  } | null
}

export type IssueState = 'CLOSED' | 'OPEN'
export type IssueStateReason = 'COMPLETED' | 'DUPLICATE' | 'NOT_PLANNED' | 'REOPENED'

type PropsFromItemPicker = Partial<
  Pick<
    ItemPickerProps<IssuePickerItem>,
    'className' | 'selectionVariant' | 'subtitle' | 'notice' | 'insidePortal' | 'responsiveOnNarrow'
  >
>

type IssuePickerBaseProps = {
  anchorElement: (props: React.HTMLAttributes<HTMLElement>) => React.ReactElement
  onIssueUpdate?: () => void
  onIssueSelection: (issues: IssuePickerItem[]) => void
  // onItemSelect is called when the user explicitly clicks an item (passed as onAction to each item).
  // Useful for distinguishing an intentional selection/deselection from the picker
  // being dismissed without interaction (e.g. clicking outside).
  onItemSelect?: () => void
  repositoryNameWithOwner?: string
  title?: string | React.ReactElement
  triggerOpen?: boolean
  onClose?: () => void
  selectedIssueIds?: string[]
  hiddenIssueIds?: string[]
  pickerId?: string
  withParent?: boolean
  // When true, only issues the viewer has triage-level access to (viewerCanUpdateMetadata) are
  // offered. Used for symmetric relationships like "relates to" that require triage on the target.
  requireTargetTriage?: boolean
  // Issue title used to surface semantically similar issues when there is no search term.
  semanticQuery?: string
  // Fired when the viewer picks a suggested issue, carrying where it ranked and whether it came from
  // semantic or lexical search — used to measure engagement with the suggestions.
  onSuggestionSelected?: (metadata: SuggestionSelection) => void
  // Fired once when the viewer first types a search term instead of using the initial suggestions.
  onSearchTermEntered?: () => void
  // Fired once when the initial (no-search) suggestion list is shown — the denominator for
  // measuring what fraction of sessions engage with the suggestions.
  onSuggestionsShown?: (metadata: SuggestionsShown) => void
} & PropsFromItemPicker

export type SuggestionSelection = {
  position: number
  source: 'semantic' | 'lexical'
  beforeTyping: boolean
}

export type SuggestionsShown = {
  count: number
  semanticCount: number
}

export type IssuePickerProps = IssuePickerBaseProps & {
  owner?: string
  isLoading?: boolean
}

type IssuePickerInternalProps = IssuePickerBaseProps & {
  items: Map<string, IssuePickerItem>
  isLoading: boolean
  filter: string
  setFilter: Dispatch<string>
  semanticIssueIds: Set<string>
  // Selected issues read through the server-persisted query. When set, the Relay auxiliary read is
  // skipped.
  selectedIssues?: Record<string, IssuePickerItem>
}

const selectedGroup = {groupId: 'selected', header: {title: 'Selected'}}
const suggestionGroup = {groupId: 'suggestions', header: {title: 'Suggestions', variant: 'filled'}}
const groups: ItemGroup[] = [selectedGroup, suggestionGroup]

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const IssuePickerSelectedIssuesGraphQLQuery = graphql`
  query IssuePickerSelectedIssuesQuery($ids: [ID!]!) {
    nodes(ids: $ids) {
      ... on Issue {
        id
        title
        state
        stateReason
        repository {
          id
          nameWithOwner
        }
        databaseId
        number
        url
        __typename
      }
    }
  }
`

const emptyMap = new Map<string, IssuePickerItem>()

export function IssuePicker({
  onIssueSelection,
  selectedIssueIds,
  hiddenIssueIds,
  owner,
  repositoryNameWithOwner,
  pickerId,
  isLoading: isLoadingExternally = false,
  withParent = false,
  requireTargetTriage = false,
  semanticQuery,
  ...rest
}: IssuePickerProps) {
  const [filter, setFilter] = useState('')
  const {isLoading, isError, items, semanticIssueIds} = useIssueFiltering(
    filter,
    owner,
    repositoryNameWithOwner,
    hiddenIssueIds,
    withParent,
    requireTargetTriage,
    semanticQuery,
  )

  // Render a fallback if there is an error fetching the data from the filter
  const {createFallbackComponent} = useItemPickerErrorFallback({
    errorMessage: LABELS.cantEditItems('issues'),
    anchorElement: rest.anchorElement,
    open: true,
  })

  const [repositoryOwner, repositoryName] = repositoryNameWithOwner?.split('/') ?? []
  const selectedIssuesTanStackEnabled =
    isFeatureEnabled('issue_picker_selected_issues_tanstack') && Boolean(repositoryOwner && repositoryName)
  const visibleItems = isLoadingExternally ? emptyMap : items
  const {
    selectedIssues,
    isError: isSelectedIssuesError,
    refetch: refetchSelectedIssues,
  } = useIssuePickerSelectedIssues({
    enabled: selectedIssuesTanStackEnabled,
    isLoading: isLoading || isLoadingExternally,
    items: visibleItems,
    selectedIssueIds,
    repositoryNameWithOwner,
  })

  if (isError) {
    return createFallbackComponent(() => {})
  }
  if (isSelectedIssuesError) {
    return createFallbackComponent(() => {
      void refetchSelectedIssues()
    })
  }

  return (
    <IssuePickerInternal
      // When there is an external loading state, act as if the internal query is still loading as well
      // and pass any empty items array. This helps to prevent layout shift in the UI when an external
      // query loads after we've initially rendered the UI.
      items={visibleItems}
      isLoading={isLoading || isLoadingExternally}
      {...rest}
      selectedIssueIds={selectedIssueIds}
      hiddenIssueIds={hiddenIssueIds}
      filter={filter}
      setFilter={setFilter}
      onIssueSelection={onIssueSelection}
      repositoryNameWithOwner={repositoryNameWithOwner}
      pickerId={pickerId}
      semanticIssueIds={semanticIssueIds}
      selectedIssues={selectedIssuesTanStackEnabled ? selectedIssues : undefined}
    />
  )
}

function IssuePickerInternal({
  items,
  onIssueSelection,
  anchorElement,
  setFilter,
  isLoading,
  title,
  onClose,
  triggerOpen,
  selectedIssueIds,
  filter,
  pickerId,
  selectionVariant = 'single',
  subtitle,
  className,
  notice,
  insidePortal,
  responsiveOnNarrow,
  onItemSelect,
  semanticIssueIds,
  onSuggestionSelected,
  onSearchTermEntered,
  onSuggestionsShown,
  selectedIssues,
}: IssuePickerInternalProps) {
  const environment = useRelayEnvironment()
  const [fetchedSelectedIssues, setFetchedSelectedIssues] = useState<Record<string, IssuePickerItem>>({})

  // Selection ids as of the last change, so a new selection can be diffed out for telemetry.
  const previousSelectedIdsRef = useRef<string[]>(selectedIssueIds || [])
  const hasInteractedRef = useRef(false)
  const hasTypedRef = useRef(false)
  const suggestionsShownRef = useRef(false)
  // Suggestion context captured at click time (rank, source, whether the viewer had typed), keyed by
  // issue id. Read back on commit so a selection reflects the list as it was when picked, not the
  // possibly-filtered list at close.
  const clickSnapshotsRef = useRef<Map<string, SuggestionSelection>>(new Map())

  // Keep the baseline in sync with async-loaded selections (e.g. existing relations) until the
  // viewer first interacts, so pre-selected issues are never mistaken for a fresh selection.
  useEffect(() => {
    if (!hasInteractedRef.current) previousSelectedIdsRef.current = selectedIssueIds || []
  }, [selectedIssueIds])

  // Fire once when the viewer first types a search term instead of using the initial suggestions.
  useEffect(() => {
    if (filter.trim() !== '' && !hasTypedRef.current) {
      hasTypedRef.current = true
      onSearchTermEntered?.()
    }
  }, [filter, onSearchTermEntered])

  // Fire once when the initial (no-search) suggestion list first renders — the impression that acts
  // as the denominator for suggestion engagement.
  useEffect(() => {
    if (suggestionsShownRef.current || isLoading || filter.trim() !== '' || !onSuggestionsShown) return
    const baseline = new Set(selectedIssueIds || [])
    const shownCount = [...items.keys()].filter(id => !baseline.has(id)).length
    suggestionsShownRef.current = true
    onSuggestionsShown({count: shownCount, semanticCount: semanticIssueIds.size})
  }, [isLoading, filter, items, semanticIssueIds, selectedIssueIds, onSuggestionsShown])

  const fetchSelectedIssues = useCallback(
    (ids: string[]) => {
      return new Promise<IssuePickerSelectedIssuesQuery$data>((resolve, reject) => {
        clientSideRelayFetchQueryRetained<IssuePickerSelectedIssuesQuery>({
          environment,
          query: IssuePickerSelectedIssuesGraphQLQuery,
          variables: {ids},
        }).subscribe({
          next: (fetchedData: IssuePickerSelectedIssuesQuery$data) => {
            if (fetchedData !== null) {
              resolve(fetchedData)
            }
          },
          error: (error: Error) => {
            reject(error)
          },
        })
      })
    },
    [environment],
  )

  const syncSelectedIssues = useEffectEvent(() => {
    // The server-persisted query owns the auxiliary read when it is enabled.
    if (selectedIssues) return

    async function doFetch(missingIssueIds: string[]) {
      const {nodes = []} = await fetchSelectedIssues(missingIssueIds)
      for (const node of nodes) {
        if (!node || node.__typename !== 'Issue') continue
        setFetchedSelectedIssues(loadedSelectedIssuesMap => {
          // RELAY-CLEANUP(IssuePicker): remove cast when IssuePickerSelectedIssuesQuery leaves Relay
          return {...loadedSelectedIssuesMap, [node.id]: node as IssuePickerItem}
        })
      }
    }

    // Check if the selected issues are already fetched and cache
    // them to avoid bugs when they're not returned in subsequent queries
    if (selectedIssueIds) {
      const missingIssueIds = []
      for (const selectedIssueId of selectedIssueIds) {
        const foundSelectedIssue = items.get(selectedIssueId)

        if (foundSelectedIssue) {
          setFetchedSelectedIssues(loadedSelectedIssuesMap => {
            return {...loadedSelectedIssuesMap, [selectedIssueId]: foundSelectedIssue}
          })
        } else if (!fetchedSelectedIssues?.[selectedIssueId]) {
          missingIssueIds.push(selectedIssueId)
        }
      }

      if (missingIssueIds.length > 0) {
        doFetch(missingIssueIds)
      }
    }
  })

  useEffect(() => {
    // Wait until the data is fetched before fetching any missing selected issues
    if (isLoading) return

    // We only need to run this after the initial query, as we'll have
    // cached the selected issues for subsequent queries.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    syncSelectedIssues()
  }, [isLoading, items])

  // Merge the fetched selected issues with the queried issues and ensure there are no duplicates
  const mergedItems = new Map(items)
  for (const [id, issue] of Object.entries(selectedIssues ?? fetchedSelectedIssues)) {
    mergedItems.set(id, issue)
  }

  const allIssues = Array.from(mergedItems.values())

  // If the search term is a number and there is an issue with that number, move it to the front.
  // If the issue is already in the 0th position, do nothing.
  const foundIssueNumberIndex = allIssues.findIndex(i => i.number === Number(filter))
  if (foundIssueNumberIndex > 0) {
    const [issue] = allIssues.splice(foundIssueNumberIndex, 1)

    if (issue) {
      allIssues.unshift(issue)
    }
  }

  // Tracking refs let the click handler read the latest suggestion state without recreating the
  // per-item props each render.
  const allIssuesRef = useLayoutTrackingRef(allIssues)
  const semanticIssueIdsRef = useLayoutTrackingRef(semanticIssueIds)
  const baselineSelectedRef = useLayoutTrackingRef(selectedIssueIds || [])

  // Snapshot the suggestion the viewer just clicked while the on-screen list is still what they saw.
  // Firing is deferred to commit (below) so only issues actually added are reported.
  const captureSuggestionContext = useCallback(
    (issue: IssuePickerItem) => {
      if (!onSuggestionSelected) return
      const baseline = baselineSelectedRef.current
      // Rank within the pickable suggestions (excluding already-selected items).
      const suggestionIds = allIssuesRef.current.filter(i => !baseline.includes(i.id)).map(i => i.id)
      clickSnapshotsRef.current.set(issue.id, {
        // -1 when the click isn't among the shown suggestions (e.g. re-selecting a pre-existing relation).
        position: suggestionIds.indexOf(issue.id),
        source: semanticIssueIdsRef.current.has(issue.id) ? 'semantic' : 'lexical',
        // "Has not typed a search term yet this session", not "the input is currently empty".
        beforeTyping: !hasTypedRef.current,
      })
    },
    [onSuggestionSelected, allIssuesRef, semanticIssueIdsRef, baselineSelectedRef],
  )

  const handleSelectionChange = useCallback(
    (issues: IssuePickerItem[]) => {
      const previousIds = previousSelectedIdsRef.current
      const addedIssues = issues.filter(issue => !previousIds.includes(issue.id))
      if (onSuggestionSelected) {
        for (const added of addedIssues) {
          const snapshot = clickSnapshotsRef.current.get(added.id)
          if (snapshot) onSuggestionSelected(snapshot)
        }
      }
      hasInteractedRef.current = true
      previousSelectedIdsRef.current = issues.map(issue => issue.id)
      onIssueSelection(issues)
    },
    [onIssueSelection, onSuggestionSelected],
  )

  const convertToItemProps = useCallback(
    (issue: IssuePickerItem) => {
      const {icon: Icon, fill} = getIssueIconAndFill(issue.state, issue.stateReason)
      const issueIsSelected = selectedIssueIds?.includes(issue.id)

      return {
        id: issue.id,
        text: issue.title,
        className: styles.issueItem,
        source: issue,
        groupId: issueIsSelected ? selectedGroup.groupId : suggestionGroup.groupId,
        leadingVisual: () => (
          <span style={{color: fill}}>
            <Icon size={16} />
          </span>
        ),
        trailingVisual: () => <span className={styles.issueNumberText}>{`#${issue.number}`}</span>,
        // Show NWO when the issue is selected as it might reside in another repository than what is being queried.
        description: issueIsSelected ? issue.repository.nameWithOwner : undefined,
        descriptionVariant: 'block' as const,
        // Only wrap onAction when telemetry is wired, so other pickers keep the original reference.
        onAction: onSuggestionSelected
          ? () => {
              captureSuggestionContext(issue)
              onItemSelect?.()
            }
          : onItemSelect,
      }
    },
    [selectedIssueIds, onItemSelect, onSuggestionSelected, captureSuggestionContext],
  )

  const issuePickerRef = useRef<HTMLButtonElement>(null)

  const itemPickerGroups = useMemo(
    () => (selectedIssueIds?.length ? groups : groups.filter(group => group.groupId !== selectedGroup.groupId)),
    [selectedIssueIds],
  )

  const showNoMatchMessage = useMemo(() => allIssues.length === 0, [allIssues.length])
  return (
    <ItemPicker
      loading={isLoading}
      items={allIssues}
      initialSelectedItems={selectedIssueIds || []}
      filterItems={setFilter}
      title={title || LABELS.issueHeader}
      getItemKey={getItemKey}
      convertToItemProps={convertToItemProps}
      placeholderText="Search issues"
      selectionVariant={selectionVariant}
      onSelectionChange={handleSelectionChange}
      renderAnchor={anchorElement}
      height="large"
      width="medium"
      maxVisibleItems={-1}
      resultListAriaLabel="issue results"
      selectPanelRef={issuePickerRef}
      triggerOpen={triggerOpen}
      groups={itemPickerGroups}
      onClose={onClose}
      pickerId={pickerId}
      subtitle={subtitle}
      className={className}
      notice={notice}
      suppressNoMatchItem
      improvedNoMatchAccessibility={showNoMatchMessage}
      noMatchMessage={
        showNoMatchMessage
          ? {
              title: 'No issues found',
              body: 'Try changing your search term or selecting a different repository.',
              variant: 'empty',
            }
          : undefined
      }
      insidePortal={insidePortal}
      responsiveOnNarrow={responsiveOnNarrow}
    />
  )
}
